import { StyledProps, StyleProps } from '@front10';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
//-------------------------------------
// Integration Layer Default Actions
//-------------------------------------
import { getAirports as getAirportsFunction } from '../../../_integrations/everymundo/airports';
import { getFares as getFaresFunction } from '../../../_integrations/everymundo/fares';
import { getNames } from '../../../_integrations/everymundo/map';
//-------------------------------------
// Atoms
//-------------------------------------
import Row from '../../elements/Row/Row';
import Box from '../../elements/Box/Box';
import SecondaryFilters from '../../controls/SecondaryFilters/ReducedSecondaryFilter';
import Col from '../../elements/Col/Col';
import Disclaimer from '../../others/Disclaimer';
import Skeleton from '../../others/Skeleton/SkeletonInteractiveMap';
import InlineFilters from '../../controls/InlineFilters/ReducedInlineFilter';
import withCommonEMContainer from '../_commons/CommonEMContainer/CommonEMContainer';
import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/groupedRoutesAggregation';
import HeaderContainer from '../../elements/HeaderContainer';
import InteractiveMap from './InteractiveMap';
import TravelRestrictionContainer from '../TravelRestrictionContainer';
//-------------------------------------
// PROPS DEFINITION
//-------------------------------------
import { VectorMapProps } from './VectorMap';
import { TextProps } from '../../elements/Text/Text';
//-------------------------------------
// HOOKS
//-------------------------------------
import useFaresApi from './filters/useFares';
import useFilters from './filters/useFilters';
import useFiltersDisableSettings from './filters/useFiltersDisabled';
import useSyncFiltersFaresApi from './filters/useSyncFiltersFareApi';
import useCountriesGeoData from './hooks/useCountriesData';
import useNameGeoData from './hooks/useNameGeoData';
import { useStore, visibleDestinationsSelector, getZoomSelector } from './hooks/useIMCState';
import { withQueryClient } from '@front10/container-elements';

export interface TranslationLabels {
  /**
   * title translation key
   */
  titleKey: string;
  /**
   * subTitle translation key
   */
  subTitleKey: string;
}

export interface UIDisplay {
  /**
   * show or hide filters
   */
  showFilters: boolean;
  /**
   * show or hide secondary filters
   */
  showSecondaryFilters: boolean;
  /**
   * Shows Loading Component
   */
  showLoading: boolean;
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: boolean;
  /**
   * Determine if the component will show the disclaimer
   */
  showDisclaimer: boolean;
  /**
   * show or hide title
   */
  showTitle: boolean;
  /**
   * show or hide subtitle
   */
  showSubTitle: boolean;
}

export interface Components {
  /**
   * pagination props
   */
  paginationSettings: PaginationSettings;
  /**
   * title props
   */
  titleSettings: $TSFixMe;
  /**
   * title props
   */
  subTitleSettings: $TSFixMe;
  /**
   * title container props
   */
  titleContainerSettings: $TSFixMe;
  /**
   * subTitle container props
   */
  subHeaderContentSettings: $TSFixMe;
  /**
   * filters props
   */
  filters: $TSFixMe;
  /**
   * secondaryFilters props
   */
  secondaryFilters: $TSFixMe;
  /**
   * loading component props
   */
  loading: StyledProps<$TSFixMe>;
  /**
   * skeleton screen props
   */
  skeletonSettings: StyledProps<$TSFixMe>;
  /**
   * disclaimer props
   */
  disclaimerSettings: StyledProps<$TSFixMe>;
  /**
   * main content props
   */
  mainContentSettings: StyledProps<$TSFixMe>;
  /**
   * alertLoading error props
   */
  alertLoading: StyledProps<$TSFixMe>;
  /**
   * alert error props
   */
  alertError: StyledProps<$TSFixMe>;
  /**
   * alert no result props
   */
  alertNoResults: StyledProps<$TSFixMe>;
  /**
   * Props to fare list
   */
  fareItem: StyledProps<$TSFixMe>;
  /**
   * popular destinations container props
   */
  popularDestinationsContainerSettings: StyleProps;
  /**
   * popular destinations label props
   */
  popularDestinationsLabelSettings: TextProps;
  /**
   * popular destinations value props
   */
  popularDestinationsValueSettings: TextProps;
  /**
   * map container col props
   */
  mapContainerSettings: StyledProps<$TSFixMe>;
  /**
   * The height of the map's area
   */
  mapHeight?: number | string;
  /**
   * vector map props
   */
  vectorMapSettings: VectorMapProps;
  /**
   * initial map center
   */
  regionCenter?: 'africa' | 'asia' | 'europa' | 'northAmerica' | 'southAmerica' | [number, number];
  /**
   * geoJSONUrl data for map draw
   */
  vectorJSONUrl?: string;
  /**
   * geoJSONUrl request options
   */
  vectorJSONUrlAxiosWrapperOptions?: $TSFixMe;
}

export interface MainProps extends WithEMContainerProps {
  /**
   * select origin text settings
   */
  selectOriginTextSettings: TextProps;
  /**
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClick: $TSFixMe;
  /**
   * Possible ways to get fares from API
   */
  faresTransformationLogic: 'grouped-routes' | 'aggregation';
  /**
   * Function to getFares
   */
  getFares: $TSFixMe;
  /**
   * Function for request to obtain airports
   */
  getAirports: $TSFixMe;
  /**
   * Component Initial Fares
   */
  initialFares: $TSFixMe[];
  /**
   * Component Behavior when there are not results available
   */
  noFaresBehavior: NoFaresBehavior;
  /**
   * Component PrePopulation
   */
  prePopulation: $TSFixMe;
  /**
   * All integration configurations
   */
  meta: $TSFixMe;
  /**
   * All integration configurations
   */
  integrationSettings: $TSFixMe;
  /**
   * Direction
   */
  direction: 'ltr' | 'rtl';
  /**
   * translate function
   */
  translate: $TSFixMe;
  /**
   * All integration configurations
   */
  theme: $TSFixMe;
  /**
   * hide budget on Multi Currency
   */
  hideBudgetOnMultiCurrency: boolean;
  /**
   *reference price for the budget
   */
  budgetSource: 'TOTAL_USD_PRICE' | 'TOTAL_PRICE' | undefined;
  /**
   * disclaimerContentSettings
   */
  disclaimerContentSettings: $TSFixMe;
  /**
   * show Travel Restriction feature
   */
  showTravelRestriction: boolean;
  /**
   * enable names map services
   */
  enableNameService?: boolean;
  /**
   * travelRestriction settings
   */
  travelRestrictionSettings?: $TSFixMe;
  /**
   * params extra send into tracking data
   */
  paramsTrackingExtra?: $TSFixMe;
  /**
   * Enable SEO in the component
   */
  enableSEO?: Boolean;
}

export type InteractiveMapContainerProps = StyledProps<
  MainProps & Partial<Components> & Partial<UIDisplay> & TranslationLabels
>;

const POINTS_STYLE_BY_MODE: any = {
  standard: {
    style: {
      background: '#4060F7',
      border: '1px solid #EEEEEE',
      'box-sizing': 'border-box',
      'box-shadow': '0px 2px 3px rgba(0, 0, 0, 0.25)'
    }
  },
  dark: {
    style: {
      background: '#4060F7',
      border: '1px solid #EEEEEE',
      'box-sizing': 'border-box',
      'box-shadow': '0px 2px 3px rgba(0, 0, 0, 0.25)'
    }
  },
  light: {
    style: {
      background: '#4060F7',
      border: '1px solid #757575',
      'box-sizing': 'border-box',
      'box-shadow': '0px 2px 3px rgba(0, 0, 0, 0.25)'
    }
  }
};

const InteractiveMapContainer: FunctionComponent<InteractiveMapContainerProps> = (
  props: InteractiveMapContainerProps
) => {
  const {
    faresTransformationLogic = 'grouped-routes',
    filters = {
      availableFilters: ['OriginDestination'],
      originDestinationSettings: {
        showOrigin: true,
        showDestination: true
      }
    },
    secondaryFilters = {},
    disclaimerSettings = {},
    skeletonSettings = {
      style: { backgroundColor: 'white' }
    },
    mainContentSettings,
    showFilters = true,
    showSecondaryFilters = true,
    showSkeletonScreen = true,
    showDisclaimer = true,
    initialFares,
    integrationSettings,
    meta,
    pageContext,
    getFares,
    getAirports,
    prePopulation,
    translate,
    direction,
    theme,
    hideBudgetOnMultiCurrency,
    budgetSource,
    disclaimerContentSettings,
    vectorJSONUrl = 'https://em-frontend-assets.airtrfx.com/assets/map-tiles/world-countries-usa-states.json',
    vectorJSONUrlAxiosWrapperOptions = {},
    mapHeight = 560,
    showTravelRestriction = false,
    enableNameService = false,
    travelRestrictionSettings = {},
    paramsTrackingExtra,
    enableSEO = true,
    ...interactiveMapProps
  } = props;
  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: 600 }, style);

  const geoData = useCountriesGeoData(vectorJSONUrl, vectorJSONUrlAxiosWrapperOptions);
  const namesData = useNameGeoData(
    getNames,
    { ...prePopulation, enableNameService, zoom: useStore(getZoomSelector) },
    integrationSettings,
    meta,
    pageContext
  );
  const filtersState = useFilters({
    filter: { prePopulation, integrationSettings, meta, pageContext, getAirports },
    filterSettings: { ...filters, ...secondaryFilters },
    availableFilters: showFilters ? filters.availableFilters : [],
    availableSecondaryFilters: showSecondaryFilters ? secondaryFilters.availableFilters : [],
    hideBudgetOnMultiCurrency,
    showInitialData:
      props.vectorMapSettings?.showInitialData === undefined ||
      props.vectorMapSettings?.showInitialData === null
        ? true
        : props.vectorMapSettings?.showInitialData
  });

  const tracking = filtersState.tracking;

  const origin = filtersState.locations.origin.value;
  const visibleDestinations = useStore(visibleDestinationsSelector);

  const fares = useFaresApi({
    getFares,
    prePopulation,
    integrationSettings,
    faresTransformationLogic,
    budgetSource,
    meta,
    pageContext,
    paginationSettings: props.paginationSettings,
    filters: {
      origins: origin ? [origin] : undefined,
      destinations: visibleDestinations === null ? [] : visibleDestinations,
      budget: filtersState.budget.value,
      dateRange: filtersState.dateRange.value,
      flightType: filtersState.flightType.value,
      sorting: filtersState.sorting.value,
      travelClasses: filtersState.travelClass.value,
      flightStopCount: filtersState.flightStopCount.value,
      travelerInterest: filtersState.travelerInterest?.selectedItems,
      paramsTrackingExtra
    }
  });

  const extendedFiltersModel = useSyncFiltersFaresApi(
    filtersState,
    {
      clearAllOnOriginChange: true
    },
    prePopulation
  );

  const isOriginSelected = Boolean(origin);
  const filtersDisableSettings = useFiltersDisableSettings(filters, {
    dateRange: !isOriginSelected,
    budget: !isOriginSelected
  });

  const secondaryFiltersDisableSettings = useFiltersDisableSettings(
    { ...filters, ...secondaryFilters },
    {
      dateRange: !isOriginSelected,
      budget: !isOriginSelected,
      flightType: !isOriginSelected,
      travelClass: !isOriginSelected,
      travelerInterest: !isOriginSelected,
      flightStopCount: !isOriginSelected
    }
  );

  const renderFilters = showFilters && (
    <InlineFilters
      {...filters}
      {...filtersState}
      {...extendedFiltersModel}
      {...filtersDisableSettings}
    />
  );

  const renderSecondaryFilters = showSecondaryFilters && (
    <SecondaryFilters
      {...filters}
      {...filtersState}
      {...secondaryFilters}
      {...secondaryFiltersDisableSettings}
      availableFilters={filtersState.availableSecondaryFilters}
      filter={{ ...{ prePopulation, integrationSettings, meta, getAirports } }}
    />
  );

  const renderDisclaimer = showDisclaimer && (
    <Row {...disclaimerContentSettings}>
      <Col>
        <Disclaimer labelKey="cmp-interactive-map-container-disclaimer" {...disclaimerSettings} />
      </Col>
    </Row>
  );

  const isSkeletonShow =
    showSkeletonScreen && (geoData.isLoading || filtersState.locations.origin.isLoading);

  const handleSeeMoreDeals = useCallback(() => {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }, [tracking]);

  const mapFilters = useMemo(() => ({ ...filtersState, ...extendedFiltersModel }), [
    extendedFiltersModel,
    filtersState
  ]);

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  const newPointsStyle = props?.vectorMapSettings?.mapMode?.mode
    ? props.vectorMapSettings?.mapMode?.style?.point
      ? props.vectorMapSettings?.mapMode?.style?.point
      : POINTS_STYLE_BY_MODE[props?.vectorMapSettings?.mapMode?.mode]?.style
    : mainContentSettings['cmp-styles']['--point--marker-pin'];

  const newMainContentSettings = props.vectorMapSettings?.mapMode
    ? {
        ...mainContentSettings,
        'cmp-styles': {
          ...mainContentSettings['cmp-styles'],
          '--point--marker-pin': {
            ...newPointsStyle
          }
        }
      }
    : mainContentSettings;

  return (
    <>
      <Box
        position="relative"
        className={`interactive-map-container ${className}`}
        style={myStyle}
        dataTestId="InteractiveMapContainer"
      >
        {isSkeletonShow && <Skeleton {...skeletonSettings} position="absolute" width="100" />}
        {!isSkeletonShow && (
          <>
            <HeaderContainer
              defaultTitleValue={'INTERACTIVE MAP MODULE'}
              handleSeeMoreDeals={handleSeeMoreDeals}
              {...(props as any)}
            />
            <Row
              gutters
              position="relative"
              {...newMainContentSettings}
              style={{ minHeight: mapHeight, ...mainContentSettings?.style }}
            >
              <InteractiveMap
                renderFilters={renderFilters}
                renderSecondaryFilters={renderSecondaryFilters}
                height={mapHeight}
                filters={mapFilters}
                fares={fares}
                geoData={geoData}
                namesData={namesData}
                dataForMarkerAction={{
                  getFares,
                  prePopulation,
                  integrationSettings,
                  faresTransformationLogic,
                  meta,
                  pageContext,
                  cache: true,
                  paginationSettings: props.paginationSettings,
                  filters: {
                    origins: origin ? [origin] : undefined,
                    destinations: visibleDestinations === null ? [] : visibleDestinations,
                    budget: filtersState.budget.value,
                    dateRange: filtersState.dateRange.value,
                    flightType: filtersState.flightType.value,
                    sorting: filtersState.sorting.value,
                    travelClasses: filtersState.travelClass.value,
                    flightStopCount: filtersState.flightStopCount.value
                  }
                }}
                {...interactiveMapProps}
              />
            </Row>
            {renderDisclaimer}
          </>
        )}
      </Box>
      {showTravelRestriction && (
        <TravelRestrictionContainer {...props} {...travelRestrictionSettings} />
      )}
    </>
  );
};

InteractiveMapContainer.defaultProps = {
  titleKey: '',
  subTitleKey: '',
  titleContainerSettings: {},
  subHeaderContentSettings: {},
  titleSettings: {
    margin: '0',
    textAlign: 'left'
  },
  subTitleSettings: {
    margin: '0',
    textAlign: 'left'
  },
  showTitle: true,
  showSubTitle: false,
  getFares: getFaresFunction,
  getAirports: getAirportsFunction,
  prePopulation: prePopulationDefaultProps,
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'InteractiveMapModule'
  },
  meta: null,
  integrationSettings: {},
  fareItem: {}
};

InteractiveMapContainer.displayName = 'InteractiveMapContainer';

export default withCommonEMContainer(withQueryClient(InteractiveMapContainer));
